import { createContext, FC, useContext, useMemo } from 'react';

import { CompanyRoles, CompanyStatus } from '@apis/companiesApi.types';
import { ContactRoles, ContactStatus } from '@apis/contactsApi.types';
import { FeeStatus } from '@apis/feesApi.types';
import { RequirementStatus } from '@apis/requirementsApi.types';
import { TaskFrequency } from '@apis/tasksApi.types';
import { UserRoles } from '@apis/usersApi.types';
import { useAgencyConstants, useConfig } from '@core/contexts/ConfigContext';
import { t } from '@core/i18n';
import { GroupedOption } from '@shared/components/group-select-new';
import { ShortWeekDay } from '@shared/constants/date';
import { CoreOptionsProvider, useCoreOptions, UseCoreOptionsResult } from '@shared/contexts/OptionsContext';
import { mapStringRecord } from '@shared/utils/common';
import {
  filterOptions,
  groupOptionsByBuildingType,
  idArrayToNumberOptions,
  objectToNumberOptions,
  objectToOptions,
  OptionsItem,
  optionsToFlatGroupOptions,
  societiesToSelectOptions,
  stringObjectToNumberOptions,
  stringObjectToOptions,
} from '@shared/utils/options';

interface UseOptionsResult extends UseCoreOptionsResult {
  accuracyOptions: OptionsItem<string>[];
  acquisitionIntelStatusOptions: OptionsItem<RequirementStatus>[];
  advisoryStatusOptions: OptionsItem<number>[];
  advisoryTypeOptions: OptionsItem<number>[];
  alienationOptions: OptionsItem<number>[];
  archiveRadioOptions: OptionsItem[];
  availabilityMonthOptions: OptionsItem[];
  availableBuildingTypeGroupOptions: GroupedOption<string>[];
  availableLettingTypeOptions: OptionsItem<number>[];
  availableMeasureOptions: OptionsItem[];
  availableTaskTypeOptions: OptionsItem[];
  bulkStatusOptions: OptionsItem<number>[];
  companyRoleOptions: OptionsItem<CompanyRoles>[];
  companyStatusOptions: OptionsItem<CompanyStatus>[];
  companyTagOptions: OptionsItem<number>[];
  companyTypeOptions: OptionsItem<string>[];
  confirmedEventOptions: OptionsItem[];
  consentSourceOptions: OptionsItem[];
  contactRoleOptions: OptionsItem<ContactRoles>[];
  contactStatusOptions: OptionsItem<ContactStatus>[];
  contactTagOptions: OptionsItem<number>[];
  contactTypeOptions: OptionsItem[];
  controlledByOptions: OptionsItem<string>[];
  countryOptions: OptionsItem<string>[];
  creatableAdvisoryStatusOptions: OptionsItem[];
  creatableTaskTypeOptions: OptionsItem<string>[];
  devPipelineFundingOptions: OptionsItem[];
  devPipelineLikelihoodOptions: OptionsItem<string>[];
  devPipelineStatusOptions: OptionsItem[];
  devPipelineTypeOptions: OptionsItem[];
  diaryTaskStatusOptions: OptionsItem[];
  disposalBreakdownOptions: OptionsItem<number>[];
  disposalCompanyRoleOptions: OptionsItem<CompanyRoles>[];
  disposalConditionTypeOptions: OptionsItem[];
  disposalContactRoleOptions: OptionsItem<ContactRoles>[];
  disposalPublishedToOptions: OptionsItem[];
  disposalSaleTypeOptions: OptionsItem<string>[];
  disposalStatusOptions: OptionsItem[];
  disposalTagOptions: OptionsItem<number>[];
  disposalTermsOfEngagementOptions: OptionsItem<string>[];
  egRankingsLeaseOptions: OptionsItem<string>[];
  egRankingsSaleOptions: OptionsItem<string>[];
  epcBandRangeOptions: OptionsItem<string>[];
  epcExemptionReasonOptions: OptionsItem<number>[];
  estimatedOptions: OptionsItem<NumberBoolean>[];
  eventNotificationOptions: OptionsItem[];
  feeCurrencyOptions: OptionsItem[];
  feesAdvisoryStages: OptionsItem<number>[];
  feesAllStagesOptions: OptionsItem<number>[];
  feesBasisOptions: OptionsItem[];
  feesDisposalLeaseStagesOptions: OptionsItem<number>[];
  feesPaymentStatusOptions: OptionsItem<number>[];
  feesRequirementLeaseStagesOptions: OptionsItem<number>[];
  feesSourceCrossSellExternalOptions: OptionsItem[];
  feesSourceCrossSellInternalOptions: OptionsItem[];
  feesSourceOptions: OptionsItem<number>[];
  feesSourceOwnActivityOptions: OptionsItem[];
  feesStatusOptions: OptionsItem<FeeStatus>[];
  feesTypeOptions: OptionsItem[];
  filterableBulkStatusOptions: OptionsItem<number>[];
  floorUnitAvailabilityColumnOptions: OptionsItem[];
  floorUnitNameOptions: OptionsItem<string>[];
  includeInstructionOptions: OptionsItem[];
  investmentCurrencyOptions: OptionsItem<string>[];
  investmentCurrencySymbolOptions: OptionsItem<string>[];
  investmentGradeOptions: OptionsItem[];
  investmentMarketStatusOptions: OptionsItem<number>[];
  investmentMetricOptions: OptionsItem[];
  investorRankingOptions: OptionsItem[];
  investorRegionOptions: OptionsItem[];
  investorTypeOptions: OptionsItem[];
  interestScheduleMatchRequestSettings: OptionsItem<number>[];
  interestScheduleOutboundSocietyMatchRequestsSettings: OptionsItem<number>[];
  interestSchedulePortalSettings: OptionsItem<number>[];
  jointExclusiveInstructionOptions: OptionsItem[];
  knownTenantOptions: OptionsItem<string>[];
  kycCompletedOptions: OptionsItem[];
  leaseActivityStatusOptions: OptionsItem[];
  leaseBreakTypeOptions: OptionsItem[];
  leaseExpiryReasonOptions: OptionsItem[];
  legalFeesApplicableOptions: OptionsItem<string>[];
  lettingTransactionCompanyLandlordRoleOptions: OptionsItem<CompanyRoles>[];
  lettingTransactionCompanyTenantRoleOptions: OptionsItem<CompanyRoles>[];
  lettingTransactionContactLandlordRoleOptions: OptionsItem<ContactRoles>[];
  lettingTransactionContactTenantRoleOptions: OptionsItem<ContactRoles>[];
  lettingTransactionTagOptions: OptionsItem<number>[];
  lettingTransactionUserLandlordRoleOptions: OptionsItem<UserRoles>[];
  lettingTransactionUserTenantRoleOptions: OptionsItem<UserRoles>[];
  marketingListOptions: OptionsItem[];
  marketingRegionOptions: OptionsItem<string>[];
  matchDisposalsSortOptions: OptionsItem[];
  metricOptions: OptionsItem<Metric>[];
  nationalityOptions: OptionsItem[];
  nineteenFiftyFourActOptions: OptionsItem<string>[];
  officesOptions: OptionsItem<number>[];
  openEventOptions: OptionsItem[];
  parkingChargeMetricOptions: OptionsItem<Metric>[];
  planningStatusOptions: OptionsItem[];
  propertyCompanyRoleOptions: OptionsItem<CompanyRoles>[];
  publishedToSocietyOptions: OptionsItem<string>[];
  quarterOptions: OptionsItem<string>[];
  reminderDaysOptions: OptionsItem<number>[];
  reminderMonthsOptions: OptionsItem<number>[];
  rentAdjustmentReasonsOptions: OptionsItem<string>[];
  rentBusinessRateOptions: OptionsItem<string>[];
  rentMetricOptions: OptionsItem<Metric>[];
  rentTypeOptions: OptionsItem[];
  repairsInsuranceOptions: OptionsItem<string>[];
  requirementContactTenantRoleOptions: OptionsItem<ContactRoles>[];
  requirementCompanyTenantRoleOptions: OptionsItem<CompanyRoles>[];
  requirementMatchRadiusOptions: OptionsItem[];
  requirementTenureTypeOptions: OptionsItem<string>[];
  requirementTypeOptions: OptionsItem<string>[];
  internalConfidentialityOptions: OptionsItem<string>[];
  saleTransactionCompanyRoleOptions: OptionsItem<CompanyRoles>[];
  saleTransactionContactRoleOptions: OptionsItem<ContactRoles>[];
  saleTransactionTagOptions: OptionsItem<number>[];
  saleTransactionUserRoleOptions: OptionsItem<UserRoles>[];
  saleTypeOptions: OptionsItem<string>[];
  scheduleOfConditionOptions: OptionsItem<string>[];
  segmentOptions: OptionsItem[];
  showEventOptions: OptionsItem[];
  societyDisposalReportTypeOptions: OptionsItem[];
  societyOptions: OptionsItem[];
  societyPropertyTypeOptions: OptionsItem<string>[];
  societyRegionOptions: OptionsItem[];
  tagOptions: OptionsItem[];
  taskRepeatOptions: OptionsItem<TaskFrequency>[];
  teamOptions: OptionsItem<number>[];
  teamSegmentOptions: OptionsItem[];
  teamStatusOptions: OptionsItem[];
  tenureOptions: OptionsItem<string>[];
  transactionStatusOptions: OptionsItem<number>[];
  vatApplicableOptions: OptionsItem<string>[];
  videoTypeOptions: OptionsItem<string>[];
  weekDayOptions: OptionsItem<ShortWeekDay>[];
  zonesOptions: OptionsItem<string>[];
  // ---- Grouped Options ----
  groupedDisposalTagOptions: GroupedOption<number>[];
}

export const OptionsContext = createContext<UseOptionsResult>({} as UseOptionsResult);

export const useOptions = () => {
  return useContext(OptionsContext);
};

const OptionsProvider: FC = ({ children }) => {
  const coreOptions = useCoreOptions();

  const { offices, segments, team_segments, teams, user } = useConfig();
  const { constants } = useAgencyConstants();

  // ---- All ----

  const allAdvisoryStatusOptions = useMemo(
    () => idArrayToNumberOptions(constants.advisoryStatusOptions),
    [constants.advisoryStatusOptions]
  );

  const allAvailableMeasureOptions = useMemo(
    () => objectToOptions(constants.availableMeasures),
    [constants.availableMeasures]
  );

  const allTaskTypeOptions = useMemo(() => objectToOptions(constants.taskTypes), [constants.taskTypes]);

  // ---- Available ----

  const availableTaskTypeOptions = useMemo(
    () => filterOptions(allTaskTypeOptions, user.organisation?.settings.task_types_visible, true),
    [allTaskTypeOptions, user.organisation?.settings.task_types_visible]
  );

  // ---- Referenced Options ----

  const disposalTagOptions = useMemo(
    () => stringObjectToNumberOptions(constants.disposalTags),
    [constants.disposalTags]
  );

  // ---- Options ----

  const result: UseOptionsResult = {
    ...coreOptions,

    accuracyOptions: useMemo(
      () => [
        { id: 'estimated', label: t('estimated') },
        { id: 'confirmed', label: t('confirmed') },
      ],
      []
    ),
    acquisitionIntelStatusOptions: useMemo(
      () => idArrayToNumberOptions(constants.acquisitionIntelStatusOptions),
      [constants.acquisitionIntelStatusOptions]
    ),
    advisoryStatusOptions: allAdvisoryStatusOptions,
    advisoryTypeOptions: useMemo(
      () => idArrayToNumberOptions(constants.advisoryTypeOptions),
      [constants.advisoryTypeOptions]
    ),
    alienationOptions: useMemo(
      () => [
        { id: 1, label: t('assignsublet_the_whole_sublet_part') },
        { id: 2, label: t('assignsublet_the_whole_only') },
        { id: 3, label: t('assign_the_whole_only') },
        { id: 4, label: t('sublet_the_whole_only') },
        { id: 5, label: t('sublet_part_only') },
        { id: 6, label: t('unknown') },
        { id: 7, label: t('alienation_prohibited') },
      ],
      []
    ),
    archiveRadioOptions: useMemo(
      () => [
        { id: 1, label: t('archive') },
        { id: 0, label: t('dont_archive') },
      ],
      []
    ),
    availableMeasureOptions: useMemo(
      () => filterOptions(allAvailableMeasureOptions, user.organisation?.settings.measurement_standard_visible, true),
      [allAvailableMeasureOptions, user.organisation?.settings.measurement_standard_visible]
    ),
    availabilityMonthOptions: useMemo(
      () => objectToOptions(constants.availabilityMonths),
      [constants.availabilityMonths]
    ),
    availableBuildingTypeGroupOptions: useMemo(() => groupOptionsByBuildingType(constants, user), [constants, user]),
    availableLettingTypeOptions: useMemo(
      () =>
        constants.availableLettingTypeOptions.map((label, idx) => ({
          id: idx,
          label,
        })),
      [constants.availableLettingTypeOptions]
    ),
    availableTaskTypeOptions,
    bulkStatusOptions: useMemo(() => stringObjectToNumberOptions(constants.bulkStatuses), [constants.bulkStatuses]),
    companyRoleOptions: useMemo(
      () => stringObjectToNumberOptions<CompanyRoles>(constants.companyRoles),
      [constants.companyRoles]
    ),
    companyStatusOptions: useMemo(
      () => idArrayToNumberOptions<CompanyStatus>(constants.companyStatusOptions),
      [constants.companyStatusOptions]
    ),
    companyTagOptions: useMemo(() => stringObjectToNumberOptions(constants.companyTags), [constants.companyTags]),
    companyTypeOptions: useMemo(() => stringObjectToOptions(constants.companyTypes), [constants.companyTypes]),
    confirmedEventOptions: useMemo(
      () => [
        { id: '0', label: t('provisional_1') },
        { id: '1', label: t('confirmed') },
      ],
      []
    ),
    consentSourceOptions: useMemo(
      () => [
        { id: 1, label: t('website') },
        { id: 2, label: t('verbal') },
        { id: 3, label: t('email') },
      ],
      []
    ),
    contactRoleOptions: useMemo(
      () => stringObjectToNumberOptions<ContactRoles>(constants.contactRoles),
      [constants.contactRoles]
    ),
    contactStatusOptions: useMemo(
      () => idArrayToNumberOptions<ContactStatus>(constants.contactStatusOptions),
      [constants.contactStatusOptions]
    ),
    contactTagOptions: useMemo(() => stringObjectToNumberOptions(constants.contactTags), [constants.contactTags]),
    contactTypeOptions: useMemo(() => objectToOptions(constants.contactTypes), [constants.contactTypes]),
    controlledByOptions: useMemo(
      () => stringObjectToOptions(constants.controlledByOptions),
      [constants.controlledByOptions]
    ),
    countryOptions: useMemo(() => objectToOptions(constants.countryOptions, true), [constants.countryOptions]),
    creatableAdvisoryStatusOptions: useMemo(
      () => filterOptions(allAdvisoryStatusOptions, constants.creatableAdvisoryStatusOptions),
      [allAdvisoryStatusOptions, constants.creatableAdvisoryStatusOptions]
    ),
    creatableTaskTypeOptions: useMemo(
      () => filterOptions(availableTaskTypeOptions, constants.creatableTaskTypes, true),
      [availableTaskTypeOptions, constants.creatableTaskTypes]
    ),
    devPipelineFundingOptions: useMemo(
      () => objectToOptions(constants.devPipelineFundingOptions),
      [constants.devPipelineFundingOptions]
    ),
    devPipelineLikelihoodOptions: useMemo(
      () => stringObjectToOptions(constants.devPipelineLikelihoodOptions),
      [constants.devPipelineLikelihoodOptions]
    ),
    devPipelineStatusOptions: useMemo(
      () => objectToOptions(constants.devPipelineStatusOptions),
      [constants.devPipelineStatusOptions]
    ),
    devPipelineTypeOptions: useMemo(
      () => objectToOptions(constants.devPipelineTypeOptions),
      [constants.devPipelineTypeOptions]
    ),
    diaryTaskStatusOptions: useMemo(
      () => [
        { label: t('all_statuses'), id: '' },
        { label: t('open'), id: 'open' },
        { label: t('completed'), id: 'completed' },
      ],
      []
    ),
    disposalBreakdownOptions: useMemo(
      () => stringObjectToNumberOptions(constants.disposalBreakdownOptions),
      [constants.disposalBreakdownOptions]
    ),
    disposalCompanyRoleOptions: useMemo(
      () => stringObjectToNumberOptions<CompanyRoles>(constants.disposalCompanyRoleOptions),
      [constants.disposalCompanyRoleOptions]
    ),
    disposalConditionTypeOptions: useMemo(
      () => objectToOptions(constants.disposalConditionTypes),
      [constants.disposalConditionTypes]
    ),
    disposalContactRoleOptions: useMemo(
      () => stringObjectToNumberOptions<ContactRoles>(constants.disposalContactRoleOptions),
      [constants.disposalContactRoleOptions]
    ),
    disposalPublishedToOptions: useMemo(
      () => objectToOptions(constants.disposalsPublishedToOptions),
      [constants.disposalsPublishedToOptions]
    ),
    disposalSaleTypeOptions: useMemo(
      () => [
        { label: t('investment'), id: 'investment_sale' },
        { label: t('occupational'), id: 'for_sale' },
      ],
      []
    ),
    disposalStatusOptions: useMemo(
      () => objectToOptions(constants.disposalStatusOptions),
      [constants.disposalStatusOptions]
    ),
    disposalTermsOfEngagementOptions: useMemo(
      () => stringObjectToOptions(constants.disposalTermsOfEngagementOptions),
      [constants.disposalTermsOfEngagementOptions]
    ),
    disposalTagOptions,
    egRankingsLeaseOptions: useMemo(
      () => stringObjectToOptions(constants.egRankingsLeaseOptions),
      [constants.egRankingsLeaseOptions]
    ),
    egRankingsSaleOptions: useMemo(
      () => stringObjectToOptions(constants.egRankingsSaleOptions),
      [constants.egRankingsSaleOptions]
    ),
    epcBandRangeOptions: useMemo(
      () => stringObjectToOptions(constants.epcBandRangeOptions),
      [constants.epcBandRangeOptions]
    ),
    epcExemptionReasonOptions: useMemo(
      () => [
        { id: 1, label: t('currently_being_constructed_or_undergoing_major_refurbishment') },
        { id: 2, label: t('due_for_demolition') },
        { id: 3, label: t('temporary_building_bracket_less_than_2_years_bracket') },
        {
          id: 4,
          label: t('industrial_sites_workshops_and_nonresidential_agricultural_buildings_with_low_energy_demand'),
        },
        {
          id: 5,
          label: t(
            'residential_agricultural_building_in_use_by_a_sector_covered_by_national_sectoral_agreement_on_energy_performance'
          ),
        },
        { id: 6, label: t('standalone_building_50m2') },
        { id: 7, label: t('listed_building') },
        { id: 8, label: t('place_of_worship') },
        { id: 9, label: t('no_building_present') },
        { id: 10, label: t('serviced_office_subject_to_legal_review') },
        { id: 11, label: t('epc_has_been_commissioned_will_be_available_in_28_days') },
      ],
      []
    ),
    estimatedOptions: useMemo(
      () => [
        { id: 1, label: t('estimated') },
        { id: 0, label: t('confirmed') },
      ],
      []
    ),
    eventNotificationOptions: useMemo(
      // TODO: Refactor - remove this, use booleanStringOptions
      () => [
        { id: '0', label: 'No' },
        { id: '1', label: 'Yes' },
      ],
      []
    ),
    feeCurrencyOptions: useMemo(
      () =>
        mapStringRecord(constants.feeCurrencyOptions, (option) => ({
          id: option?.iso,
          label: option?.name,
        })),
      [constants.feeCurrencyOptions]
    ),
    feesAdvisoryStages: useMemo(
      () => stringObjectToNumberOptions(constants.feesAdvisoryStages),
      [constants.feesAdvisoryStages]
    ),
    feesAllStagesOptions: useMemo(
      () => stringObjectToNumberOptions(constants.feesAllStagesOptions),
      [constants.feesAllStagesOptions]
    ),
    feesBasisOptions: useMemo(() => objectToOptions(constants.feesBasisOptions), [constants.feesBasisOptions]),
    feesDisposalLeaseStagesOptions: useMemo(
      () => stringObjectToNumberOptions(constants.feesDisposalLeaseStages),
      [constants.feesDisposalLeaseStages]
    ),
    feesPaymentStatusOptions: useMemo(
      () => idArrayToNumberOptions(constants.feesPaymentStatusOptions),
      [constants.feesPaymentStatusOptions]
    ),
    feesRequirementLeaseStagesOptions: useMemo(
      () => stringObjectToNumberOptions(constants.feesRequirementLeaseStages),
      [constants.feesRequirementLeaseStages]
    ),
    feesSourceCrossSellExternalOptions: useMemo(
      () => stringObjectToOptions(constants.feesSourceCrossSellExternalOptions),
      [constants.feesSourceCrossSellExternalOptions]
    ),
    feesSourceCrossSellInternalOptions: useMemo(
      () => stringObjectToOptions(constants.feesSourceCrossSellInternalOptions),
      [constants.feesSourceCrossSellInternalOptions]
    ),
    feesSourceOptions: useMemo(
      () => idArrayToNumberOptions(constants.feesSourceOptions),
      [constants.feesSourceOptions]
    ),
    feesSourceOwnActivityOptions: useMemo(
      () => stringObjectToOptions(constants.feesSourceOwnActivityOptions),
      [constants.feesSourceOwnActivityOptions]
    ),
    feesStatusOptions: useMemo(
      () => idArrayToNumberOptions(constants.feesStatusOptions),
      [constants.feesStatusOptions]
    ),
    feesTypeOptions: useMemo(() => objectToOptions(constants.feesTypeOptions), [constants.feesTypeOptions]),
    filterableBulkStatusOptions: useMemo(
      () => stringObjectToNumberOptions(constants.bulkStatusesFilterable),
      [constants.bulkStatusesFilterable]
    ),
    floorUnitAvailabilityColumnOptions: useMemo(
      () => objectToOptions(constants.floorUnitAvailabilityColumns),
      [constants.floorUnitAvailabilityColumns]
    ),
    includeInstructionOptions: useMemo(
      () => [
        { id: '1', label: t('include_our_instructions') },
        { id: '0', label: t('include_market_intel') },
      ],
      []
    ),
    investmentCurrencyOptions: useMemo(
      () =>
        mapStringRecord(constants.investmentCurrencyOptions, (option) => ({
          id: option?.iso,
          label: option?.name,
        })),
      [constants.investmentCurrencyOptions]
    ),
    investmentCurrencySymbolOptions: useMemo(
      () =>
        mapStringRecord(constants.investmentCurrencyOptions, (option) => ({
          id: option?.iso,
          label: option?.symbol,
        })),
      [constants.investmentCurrencyOptions]
    ),
    investmentGradeOptions: useMemo(
      () => objectToOptions(constants.investmentGradeOptions),
      [constants.investmentGradeOptions]
    ),
    investmentMarketStatusOptions: useMemo(
      () => stringObjectToNumberOptions(constants.investmentMarketStatusOptions),
      [constants.investmentMarketStatusOptions]
    ),
    investmentMetricOptions: useMemo(
      () => stringObjectToOptions(constants.investmentMetrics),
      [constants.investmentMetrics]
    ),
    investorRankingOptions: useMemo(
      () => objectToOptions(constants.investorRankingOptions),
      [constants.investorRankingOptions]
    ),
    investorRegionOptions: useMemo(
      () => objectToOptions(constants.investorRegionOptions),
      [constants.investorRegionOptions]
    ),
    investorTypeOptions: useMemo(() => objectToOptions(constants.investorTypeOptions), [constants.investorTypeOptions]),
    interestScheduleMatchRequestSettings: useMemo(
      () => stringObjectToNumberOptions(constants.interestScheduleStatusMatchRequestsOptions),
      [constants.interestScheduleStatusMatchRequestsOptions]
    ),
    interestSchedulePortalSettings: useMemo(
      () => stringObjectToNumberOptions(constants.interestScheduleStatusPortalEnquiriesOptions),
      [constants.interestScheduleStatusPortalEnquiriesOptions]
    ),
    interestScheduleOutboundSocietyMatchRequestsSettings: useMemo(
      () => stringObjectToNumberOptions(constants.interestScheduleStatusOutboundSocietyMatchRequestsOptions),
      [constants.interestScheduleStatusOutboundSocietyMatchRequestsOptions]
    ),
    jointExclusiveInstructionOptions: useMemo(
      () => [
        { id: 'exclusive_instructions', label: t('exclusive_instructions') },
        { id: 'joint_instructions', label: t('joint_instructions') },
      ],
      []
    ),
    knownTenantOptions: useMemo(
      () => [
        { id: 'known_tenant', label: t('known_tenant') },
        { id: 'unknown_tenant', label: t('unknown_tenant') },
        { id: 'market_intel', label: t('market_intel') },
      ],
      []
    ),
    kycCompletedOptions: useMemo(() => objectToOptions(constants.kycCompletedOptions), [constants.kycCompletedOptions]),
    leaseActivityStatusOptions: useMemo(
      () => [
        { id: 'active', label: t('active') },
        { id: 'inactive', label: t('inactive') },
        { id: 'unknown', label: t('unknown') },
      ],
      []
    ),
    leaseBreakTypeOptions: useMemo(
      () => [
        { id: 'landlord_tenant', label: t('landlord_and_tenant') },
        { id: 'landlord', label: t('landlord_only') },
        { id: 'tenant', label: t('tenant_only') },
      ],
      []
    ),
    leaseExpiryReasonOptions: useMemo(
      () => objectToOptions(constants.leaseExpiryReasonOptions),
      [constants.leaseExpiryReasonOptions]
    ),
    legalFeesApplicableOptions: useMemo(
      () => objectToOptions(constants.legalFeesApplicableOptions),
      [constants.legalFeesApplicableOptions]
    ),
    lettingTransactionCompanyLandlordRoleOptions: useMemo(
      () => stringObjectToNumberOptions<CompanyRoles>(constants.lettingTransactionCompanyLandlordRoles),
      [constants.lettingTransactionCompanyLandlordRoles]
    ),
    lettingTransactionCompanyTenantRoleOptions: useMemo(
      () => stringObjectToNumberOptions<CompanyRoles>(constants.lettingTransactionCompanyTenantRoles),
      [constants.lettingTransactionCompanyTenantRoles]
    ),
    lettingTransactionContactLandlordRoleOptions: useMemo(
      () => stringObjectToNumberOptions<ContactRoles>(constants.lettingTransactionContactLandlordRoles),
      [constants.lettingTransactionContactLandlordRoles]
    ),
    lettingTransactionContactTenantRoleOptions: useMemo(
      () => stringObjectToNumberOptions<ContactRoles>(constants.lettingTransactionContactTenantRoles),
      [constants.lettingTransactionContactTenantRoles]
    ),
    lettingTransactionUserLandlordRoleOptions: useMemo(
      () => stringObjectToNumberOptions<UserRoles>(constants.lettingTransactionUserLandlordRoles),
      [constants.lettingTransactionUserLandlordRoles]
    ),
    lettingTransactionUserTenantRoleOptions: useMemo(
      () => stringObjectToNumberOptions<UserRoles>(constants.lettingTransactionUserTenantRoles),
      [constants.lettingTransactionUserTenantRoles]
    ),

    lettingTransactionTagOptions: useMemo(
      () => stringObjectToNumberOptions(constants.lettingTransactionTags),
      [constants.lettingTransactionTags]
    ),

    saleTransactionTagOptions: useMemo(
      () => stringObjectToNumberOptions(constants.saleTransactionTags),
      [constants.saleTransactionTags]
    ),
    marketingListOptions: useMemo(
      () => constants.marketingLists.map((option) => ({ id: option.id, label: option.name })),
      [constants.marketingLists]
    ),
    marketingRegionOptions: useMemo(
      () => stringObjectToOptions(constants.marketingRegionOptions),
      [constants.marketingRegionOptions]
    ),
    matchDisposalsSortOptions: useMemo(
      () => [
        { id: 'published_at-desc', label: t('published_at') },
        { id: 'updated_at-desc', label: t('updated_at') },
        { id: 'size_to-desc', label: t('size_descending') },
        { id: 'size_from-asc', label: t('size_ascending') },
      ],
      []
    ),
    metricOptions: useMemo(() => stringObjectToOptions<Metric>(constants.genericMetrics), [constants.genericMetrics]),
    nationalityOptions: useMemo(() => objectToOptions(constants.nationalityOptions), [constants.nationalityOptions]),
    nineteenFiftyFourActOptions: useMemo(
      () => [
        { id: 'yes', label: t('yes') },
        { id: 'no', label: t('no') },
        { id: 'unknown', label: t('unknown') },
      ],
      []
    ),
    officesOptions: useMemo(() => idArrayToNumberOptions(offices), [offices]),
    openEventOptions: useMemo(
      () => [
        { id: '1', label: 'Yes' },
        { id: '0', label: 'No, Invite only' },
      ],
      []
    ),
    parkingChargeMetricOptions: useMemo(
      () => stringObjectToOptions<Metric>(constants.parkingChargeMetrics),
      [constants.parkingChargeMetrics]
    ),
    propertyCompanyRoleOptions: useMemo(
      () => stringObjectToNumberOptions<CompanyRoles>(constants.propertyCompanyRoleOptions),
      [constants.propertyCompanyRoleOptions]
    ),
    planningStatusOptions: useMemo(
      () => objectToOptions(constants.planningStatusOptions),
      [constants.planningStatusOptions]
    ),
    publishedToSocietyOptions: useMemo(
      () => [
        { id: 'published', label: t('published') },
        { id: 'not_published', label: t('not_published') },
      ],
      []
    ),
    quarterOptions: useMemo(
      () => [
        { id: 'Q1', label: 'Q1' },
        { id: 'Q2', label: 'Q2' },
        { id: 'Q3', label: 'Q3' },
        { id: 'Q4', label: 'Q4' },
      ],
      []
    ),
    reminderDaysOptions: useMemo(
      () => [
        { id: -1, label: t('none') },
        { id: 30, label: t('xmonthsx_month_1', { months: 1 }) },
        { id: 60, label: t('xmonthsx_months_1', { months: 2 }) },
        { id: 90, label: t('xmonthsx_months_1', { months: 3 }) },
        { id: 180, label: t('xmonthsx_months_1', { months: 6 }) },
        { id: 270, label: t('xmonthsx_months_1', { months: 9 }) },
        { id: 365, label: t('xmonthsx_months_1', { months: 12 }) },
        { id: 450, label: t('xmonthsx_months_1', { months: 15 }) },
        { id: 540, label: t('xmonthsx_months_1', { months: 18 }) },
        { id: 730, label: t('xmonthsx_months_1', { months: 24 }) },
        { id: 1095, label: t('xmonthsx_months_1', { months: 36 }) },
      ],
      []
    ),
    reminderMonthsOptions: useMemo(
      () => [
        { id: -1, label: t('none') },
        { id: 1, label: t('xmonthsx_month_1', { months: 1 }) },
        { id: 2, label: t('xmonthsx_months_1', { months: 2 }) },
        { id: 3, label: t('xmonthsx_months_1', { months: 3 }) },
        { id: 4, label: t('xmonthsx_months_1', { months: 4 }) },
        { id: 5, label: t('xmonthsx_months_1', { months: 5 }) },
        { id: 6, label: t('xmonthsx_months_1', { months: 6 }) },
        { id: 9, label: t('xmonthsx_months_1', { months: 9 }) },
        { id: 12, label: t('xmonthsx_months_1', { months: 12 }) },
        { id: 15, label: t('xmonthsx_months_1', { months: 15 }) },
        { id: 18, label: t('xmonthsx_months_1', { months: 18 }) },
        { id: 24, label: t('xmonthsx_months_1', { months: 24 }) },
        { id: 36, label: t('xmonthsx_months_1', { months: 36 }) },
      ],
      []
    ),
    // TODO: Get rent adjustment reasons added to backend, maybe?
    rentAdjustmentReasonsOptions: useMemo(
      () =>
        objectToOptions({
          'frontage-to-depth': t('frontage_to_depth'),
          'stepped-access': t('stepped_access'),
          'assumed-term': t('assumed_term'),
          quantum: t('quantum'),
          'floor-to-ceiling-height': t('floor_to_ceiling_height'),
          'lack-of-ancillary-storage': t('lack_of_ancillary_storage'),
          shape: t('shape'),
          masking: t('masking'),
          columns: t('columns'),
          'unit-visibility': t('unit_visibility'),
        }),
      []
    ),
    rentBusinessRateOptions: useMemo(
      () => [
        {
          id: 'exclusive',
          label: t('exclusive'),
          tooltip: t('rent_exclusive_of_business_rates_and_charges'),
        },
        {
          id: 'inclusive',
          label: t('inclusive'),
          tooltip: t('rent_inclusive_of_business_rates_and_charges'),
        },
        {
          id: 'na',
          label: t('na'),
          tooltip: t('not_applicable'),
        },
      ],
      []
    ),
    rentMetricOptions: useMemo(() => stringObjectToOptions<Metric>(constants.rentMetrics), [constants.rentMetrics]),
    rentTypeOptions: useMemo(() => objectToOptions(constants.rentTypes), [constants.rentTypes]),
    repairsInsuranceOptions: useMemo(
      () => [
        { id: 'fri', label: t('fri_bracket_full_repairing_and_insuring_lease_bracket') },
        { id: 'iri', label: t('iri_bracket_internal_repairing_and_insuring_lease_bracket') },
        { id: 'fs', label: t('fully_serviced') },
        { id: 'fr', label: t('full_repairing_lease') },
        { id: 'ir', label: t('internal_repairing_lease') },
        { id: 'vfri', label: t('effective_full_repairing_and_insuring_lease') },
        { id: 'unknown', label: t('unknown') },
      ],
      []
    ),
    requirementContactTenantRoleOptions: useMemo(
      () => stringObjectToNumberOptions<ContactRoles>(constants.requirementContactTenantRoles),
      [constants.requirementContactTenantRoles]
    ),
    requirementCompanyTenantRoleOptions: useMemo(
      () => stringObjectToNumberOptions<CompanyRoles>(constants.requirementCompanyTenantRoles),
      [constants.requirementCompanyTenantRoles]
    ),
    requirementListFilterOptions: useMemo(() => {
      return (constants.requirementListOptions || [])
        .filter((option) => option.can_filter)
        .map((option) => ({
          id: option.id,
          label: option.name,
        }));
    }, [constants.requirementListOptions]),
    requirementMatchRadiusOptions: useMemo(() => {
      const radiusRangeLabel =
        constants.localisation.radius_measurement === 'km'
          ? 'search_radius_xradiusx_km'
          : 'search_radius_xradiusx_miles';

      return [0, 0.25, 0.5, 1, 2, 5, 10, 25].map((radius) => ({
        id: radius.toString(),
        label: t(radiusRangeLabel, { radius }),
      }));
    }, [constants.localisation.radius_measurement]),
    requirementStatusOptions: useMemo(
      () => idArrayToNumberOptions(constants.requirementStatusOptions),
      [constants.requirementStatusOptions]
    ),
    requirementTypeOptions: useMemo(
      () => [
        { id: 'direct_enquiries', label: t('direct_enquiries') },
        { id: 'agent_led', label: t('agentled') },
        { id: 'our_acquisitions', label: t('our_acquisitions') },
        { id: 'availability_schedules', label: t('availability_schedules') },
      ],
      []
    ),
    internalConfidentialityOptions: useMemo(
      () => [
        { id: '0', label: t('not_confidential') },
        { id: '1', label: t('internal_access_restricted') },
      ],
      []
    ),
    saleTransactionCompanyRoleOptions: useMemo(
      () => stringObjectToNumberOptions<CompanyRoles>(constants.saleTransactionCompanyRoles),
      [constants.saleTransactionCompanyRoles]
    ),
    saleTransactionContactRoleOptions: useMemo(
      () => stringObjectToNumberOptions<ContactRoles>(constants.saleTransactionContactRoles),
      [constants.saleTransactionContactRoles]
    ),
    saleTransactionUserRoleOptions: useMemo(
      () => stringObjectToNumberOptions<UserRoles>(constants.saleTransactionUserRoles),
      [constants.saleTransactionUserRoles]
    ),
    saleTypeOptions: useMemo(
      () => [
        { label: t('investment'), id: '1' },
        { label: t('occupational'), id: '0' },
      ],
      []
    ),
    scheduleOfConditionOptions: useMemo(
      () => [
        { id: 'yes', label: t('yes') },
        { id: 'no', label: t('no') },
        { id: 'unknown', label: t('unknown') },
      ],
      []
    ),
    segmentOptions: useMemo(
      () =>
        mapStringRecord(segments, (option) => ({
          id: `${option.id}`,
          label: option.name,
        })),
      [segments]
    ),
    showEventOptions: useMemo(
      () => [
        { label: t('my_events'), id: 'mine' },
        { label: t('team_events'), id: 'teams' },
        { label: t('all_events'), id: '' },
      ],
      []
    ),
    societyDisposalReportTypeOptions: useMemo(
      () => [
        { id: 1, label: 'Competition Schedule' },
        { id: 2, label: 'Feasibility Study' },
        { id: 3, label: 'Market Research' },
      ],
      []
    ),
    societyOptions: useMemo(() => societiesToSelectOptions(user.societies), [user.societies]),
    societyPropertyTypeOptions: useMemo(
      () => stringObjectToOptions(constants.societyPropertyTypes),
      [constants.societyPropertyTypes]
    ),
    societyRegionOptions: useMemo(() => objectToNumberOptions(constants.societyRegions), [constants.societyRegions]),
    tagOptions: useMemo(() => constants.tags.map((item) => ({ id: item.id, label: item.name })), [constants.tags]),

    taskRepeatOptions: useMemo(
      () => [
        { label: t('doesnt_repeat'), id: TaskFrequency.None },
        { label: t('daily'), id: TaskFrequency.Daily },
        { label: t('weekly'), id: TaskFrequency.Weekly },
        { label: t('monthly'), id: TaskFrequency.Monthly },
        { label: t('annually'), id: TaskFrequency.Yearly },
      ],
      []
    ),
    teamOptions: useMemo(() => idArrayToNumberOptions(teams), [teams]),
    teamSegmentOptions: useMemo(
      () =>
        mapStringRecord(team_segments, (option) => ({
          id: `${option.id}`,
          label: option.name,
        })),
      [team_segments]
    ),
    teamStatusOptions: useMemo(
      () => [
        { id: 0, label: t('active') },
        { id: 1, label: t('scraper') },
        { id: 2, label: t('scraper_with_meta') },
        { id: 3, label: t('inactive') },
        { id: 10, label: t('suspended') },
        { id: 100, label: t('deleted') },
      ],
      []
    ),
    tenureOptions: useMemo(
      () => [
        { id: 'to_let', label: t('to_let') },
        { id: 'for_sale', label: t('occupational_sale') },
        { id: 'investment_sale', label: t('investment_sale') },
      ],
      []
    ),
    transactionStatusOptions: useMemo(
      () => stringObjectToNumberOptions(constants.transactionStatusOptions),
      [constants.transactionStatusOptions]
    ),
    vatApplicableOptions: useMemo(
      () => stringObjectToOptions(constants.vatApplicableOptions),
      [constants.vatApplicableOptions]
    ),
    videoTypeOptions: useMemo(
      () => [
        { id: 'video', label: t('video') },
        { id: '360_tour', label: t('360_tour') },
      ],
      []
    ),
    weekDayOptions: useMemo(
      () => [
        { label: t('monday'), id: ShortWeekDay.Monday },
        { label: t('tuesday'), id: ShortWeekDay.Tuesday },
        { label: t('wednesday'), id: ShortWeekDay.Wednesday },
        { label: t('thursday'), id: ShortWeekDay.Thursday },
        { label: t('friday'), id: ShortWeekDay.Friday },
        { label: t('saturday'), id: ShortWeekDay.Saturday },
        { label: t('sunday'), id: ShortWeekDay.Sunday },
      ],
      []
    ),
    zonesOptions: useMemo(
      () => [
        // TODO: Get zone options added to the backend, maybe?
        { label: t('entire_floor'), id: 'floor' },
        { label: t('zone_a'), id: 'zone_a' },
        { label: t('zone_b'), id: 'zone_b' },
        { label: t('zone_c'), id: 'zone_c' },
        { label: t('zone_d'), id: 'zone_d' },
        { label: t('remainder'), id: 'remainder' },
      ],
      []
    ),
    // ---- Grouped Options ----
    groupedDisposalTagOptions: useMemo(() => optionsToFlatGroupOptions(disposalTagOptions), [disposalTagOptions]),
  };

  return <OptionsContext.Provider value={result}>{children}</OptionsContext.Provider>;
};

export const CombinedOptionsProvider: FC = ({ children }) => (
  <CoreOptionsProvider>
    <OptionsProvider>{children}</OptionsProvider>
  </CoreOptionsProvider>
);
